import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Shield, Send, CheckCircle, User, Mail, FileText, Trash2, Download, Eye, Ban, Server, Lock, Database, Globe, Clock, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RGPDPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    requestType: '',
    details: '',
    acceptTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const requestTypes = [
    { id: 'access', label: 'Droit d\'accès', description: 'Obtenir une copie de mes données personnelles', icon: Eye },
    { id: 'rectification', label: 'Droit de rectification', description: 'Corriger mes données personnelles', icon: FileText },
    { id: 'erasure', label: 'Droit à l\'effacement', description: 'Supprimer mes données personnelles', icon: Trash2 },
    { id: 'portability', label: 'Droit à la portabilité', description: 'Récupérer mes données dans un format lisible', icon: Download },
    { id: 'opposition', label: 'Droit d\'opposition', description: 'M\'opposer au traitement de mes données', icon: Ban },
    { id: 'other', label: 'Autre demande', description: 'Autre question relative à mes données', icon: Mail }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.requestType) {
      toast.error('Veuillez sélectionner un type de demande');
      return;
    }
    
    if (!formData.acceptTerms) {
      toast.error('Veuillez accepter les conditions');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          subject: `[RGPD] ${requestTypes.find(r => r.id === formData.requestType)?.label}`,
          message: `Type de demande: ${requestTypes.find(r => r.id === formData.requestType)?.label}\n\nDétails:\n${formData.details}`,
          type: 'rgpd'
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Votre demande a été envoyée avec succès');
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer ou nous contacter par email.');
    }
    
    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow py-12">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-4">Demande envoyée !</h1>
              <p className="text-gray-600 mb-6">
                Votre demande RGPD a bien été reçue. Nous la traiterons dans un délai maximum de 30 jours 
                conformément à la réglementation en vigueur.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Un email de confirmation a été envoyé à <strong>{formData.email}</strong>
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/">
                  <Button>Retour à l'accueil</Button>
                </Link>
                <Link to="/confidentialite">
                  <Button variant="outline">Voir notre politique</Button>
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-grow py-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-3">Exercer vos droits RGPD</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Conformément au Règlement Général sur la Protection des Données (RGPD), 
              vous pouvez exercer vos droits concernant vos données personnelles en utilisant ce formulaire.
            </p>
          </div>

          {/* Navigation */}
          <div className="flex flex-wrap gap-4 mb-8 justify-center">
            <Link 
              to="/mentions-legales" 
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Mentions légales
            </Link>
            <Link 
              to="/cgu" 
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              CGU
            </Link>
            <Link 
              to="/confidentialite" 
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Confidentialité
            </Link>
            <Link 
              to="/rgpd" 
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-medium"
            >
              <Shield className="w-4 h-4" /> Formulaire RGPD
            </Link>
          </div>

          {/* Data Storage & Security Info */}
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 dark:from-teal-900/20 dark:to-blue-900/20 rounded-2xl p-6 md:p-8 mb-8 border border-teal-200 dark:border-teal-800">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
              <Database className="w-6 h-6 text-teal-600" />
              Où sont stockées vos données ?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Storage Location */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Localisation</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Serveurs situés en <strong>Europe (France/UE)</strong>, conformément au RGPD. 
                  Hébergement certifié et audité.
                </p>
              </div>

              {/* Encryption */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Chiffrement</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  <strong>AES-256</strong> au repos et <strong>TLS 1.3</strong> en transit. 
                  Vos données sont protégées à tout moment.
                </p>
              </div>

              {/* Backups */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-4">
                  <Server className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Sauvegardes</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Sauvegardes <strong>quotidiennes chiffrées</strong> avec rétention de 30 jours. 
                  Reprise d'activité garantie.
                </p>
              </div>
            </div>
          </div>

          {/* Data Ownership & Access Control */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 md:p-8 mb-8 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-teal-600" />
              Propriété et contrôle des données médicales
            </h2>
            
            <div className="space-y-6">
              {/* Data Ownership */}
              <div className="flex items-start gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-white mb-1">Propriété des données</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Les données médicales de votre animal <strong>vous appartiennent</strong>. 
                    VetoRDV agit comme <strong>hébergeur sécurisé</strong> et facilite le partage 
                    avec les vétérinaires que vous autorisez.
                  </p>
                </div>
              </div>

              {/* Access Control */}
              <div className="flex items-start gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-white mb-1">Gestion des accès</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Vous pouvez à tout moment <strong>autoriser ou retirer l'accès</strong> à votre 
                    carnet médical. Chaque vétérinaire doit être explicitement autorisé par vous 
                    pour consulter les données de votre animal.
                  </p>
                </div>
              </div>

              {/* Audit Trail */}
              <div className="flex items-start gap-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-white mb-1">Historique des accès</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Chaque consultation de votre dossier est <strong>enregistrée et traçable</strong>. 
                    Vous pouvez consulter l'historique complet : qui a consulté, quand, et quelles données.
                    <br />
                    <em className="text-sm">Exemple : "Dr Martin a consulté le dossier le 12/02/2026 à 14h30"</em>
                  </p>
                </div>
              </div>
            </div>

            {/* CTA for logged-in patients */}
            <div className="mt-6 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800 text-center">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Gérez les accès à vos données directement depuis votre espace patient
              </p>
              <Link to="/espace-patient">
                <Button className="bg-teal-600 hover:bg-teal-700">
                  <Shield className="w-4 h-4 mr-2" />
                  Accéder à mon espace
                </Button>
              </Link>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="pl-10"
                      placeholder="Votre prénom"
                      data-testid="rgpd-firstname"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="pl-10"
                      placeholder="Votre nom"
                      data-testid="rgpd-lastname"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    placeholder="votre@email.com"
                    data-testid="rgpd-email"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Utilisez l'adresse email associée à votre compte vetoRDV
                </p>
              </div>

              {/* Request Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type de demande *
                </label>
                <div className="grid md:grid-cols-2 gap-3">
                  {requestTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <label
                        key={type.id}
                        className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                          formData.requestType === type.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`rgpd-type-${type.id}`}
                      >
                        <input
                          type="radio"
                          name="requestType"
                          value={type.id}
                          checked={formData.requestType === type.id}
                          onChange={(e) => setFormData({ ...formData, requestType: e.target.value })}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${formData.requestType === type.id ? 'text-green-600' : 'text-gray-400'}`} />
                            <span className="font-medium text-gray-800">{type.label}</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Détails de votre demande
                </label>
                <textarea
                  value={formData.details}
                  onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Précisez votre demande si nécessaire..."
                  data-testid="rgpd-details"
                />
              </div>

              {/* Terms */}
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                    className="mt-1"
                    data-testid="rgpd-accept-terms"
                  />
                  <span className="text-sm text-gray-600">
                    Je certifie être la personne concernée par cette demande ou son représentant légal. 
                    Je comprends que vetoRDV pourra me demander une pièce d'identité pour vérifier mon identité 
                    avant de traiter ma demande. *
                  </span>
                </label>
              </div>

              {/* Submit */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-center pt-4">
                <p className="text-sm text-gray-500">
                  Délai de réponse : 30 jours maximum
                </p>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                  data-testid="rgpd-submit"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Envoyer ma demande
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Info Box */}
          <div className="mt-8 bg-blue-50 border border-blue-100 rounded-xl p-6">
            <h3 className="font-semibold text-blue-800 mb-2">ℹ️ Informations importantes</h3>
            <ul className="text-sm text-blue-700 space-y-2">
              <li>• Votre demande sera traitée dans un délai maximum de 30 jours.</li>
              <li>• Nous pouvons vous demander une pièce d'identité pour vérifier votre identité.</li>
              <li>• En cas de demande complexe, le délai peut être prolongé de 2 mois.</li>
              <li>• Pour toute question : <a href="mailto:dpo@vetordv.fr" className="underline">dpo@vetordv.fr</a></li>
            </ul>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RGPDPage;
